import { useState } from 'react';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@roger/ui-components';
import { ArrowLeft, ChevronDown, Pencil, X } from 'lucide-react';

import type {
  ChannelKind,
  GroupDetail,
  GroupWrite,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import {
  getBusinessErrorMessage,
  getConflictCode,
} from '../../utils/apiErrorDetail';
import { ArchiveGroupDialog } from './ArchiveGroupDialog';
import { GroupKindField } from './GroupKindField';
import { DestructiveConfirmDialog } from './DestructiveConfirmDialog';
import { EntryIcon } from './EntryIcon';
import { MemberPicker } from './MemberPicker';
import { MemberRow } from './MemberRow';
import {
  addEntry,
  buildRoster,
  entriesFromDetail,
  removeEntry,
  conversationChannelId,
  peopleCount,
  removeEntryMessage,
  rosterHeadline,
  toEntryRef,
  type EntryRef,
} from './memberEntries';
import {
  groupKeys,
  groupMutationKeys,
  useGroupDetail,
  useUpdateGroup,
} from './useGroupAdminQueries';

/** The only server messages shown inline; everything else is a generic toast. */
const INLINE_MESSAGES = new Set([
  'A group with this name already exists',
  'User not found',
  'Role not found',
]);

/** The sentence the chat panel already shows on an archived channel. */
const ARCHIVED_NOTICE = 'This conversation is archived.';

/** The thrown client error wraps the 409 body, so read the code the way
 *  `socConflictCopyFromError` does rather than keying on the 409 status: the
 *  duplicate-name refusal is a 409 too. */
function conflictCode(error: unknown): string | undefined {
  const wrapper = (error ?? {}) as {
    error?: unknown;
    response?: { data?: unknown };
  };
  return (
    getConflictCode(error) ??
    getConflictCode(wrapper.error) ??
    getConflictCode(wrapper.response?.data)
  );
}

type PendingRemove = { entry: EntryRef; title: string; message: string };

type GroupEditorDrawerProps = {
  groupId: string;
  onClose: () => void;
};

export function GroupEditorDrawer({
  groupId,
  onClose,
}: GroupEditorDrawerProps) {
  const {
    data: detail,
    isError: isDetailError,
    isFetching: isDetailFetching,
    refetch: refetchDetail,
  } = useGroupDetail(groupId);

  return (
    <Sheet open onOpenChange={isOpen => !isOpen && onClose()}>
      <SheetContent
        side='right'
        aria-describedby={undefined}
        className='flex w-full flex-col overflow-y-auto sm:max-w-none md:w-[420px] lg:w-[480px]'
      >
        <Button
          variant='ghost'
          size='icon'
          aria-label='Back'
          className='md:hidden'
          onClick={onClose}
        >
          <ArrowLeft />
        </Button>
        {detail ? (
          <EditorBody
            detail={detail}
            isDetailError={isDetailError}
            isDetailFetching={isDetailFetching}
            onRetryDetail={() => void refetchDetail()}
            onClose={onClose}
          />
        ) : (
          <SheetHeader>
            <SheetTitle />
          </SheetHeader>
        )}
      </SheetContent>
    </Sheet>
  );
}

function EditorBody({
  detail,
  isDetailError,
  isDetailFetching,
  onRetryDetail,
  onClose,
}: {
  detail: GroupDetail;
  isDetailError: boolean;
  isDetailFetching: boolean;
  onRetryDetail: () => void;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const updateGroup = useUpdateGroup();
  const groupUpdateCount = useIsMutating({
    mutationKey: groupMutationKeys.update,
    predicate: mutation =>
      (mutation.state.variables as { id?: string } | undefined)?.id ===
      detail.id,
  });
  const isGroupUpdatePending = updateGroup.isPending || groupUpdateCount > 0;
  const isGroupSaveBlocked =
    isGroupUpdatePending || isDetailFetching || isDetailError;
  const showError = useStore(state => state.showError);
  const openChatWindow = useStore(state => state.openChatWindow);
  const userId = useStore(state => state.user?.id);
  const channelId = conversationChannelId(detail, userId);

  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(detail.name);
  const [nameError, setNameError] = useState<string | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<PendingRemove | null>(
    null
  );
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archived, setArchived] = useState(false);

  const entries = entriesFromDetail(detail);

  // channel_kind is nullable on the wire: a manual group can have no live
  // channel (channel_id null), and then there is nothing to switch. The
  // control renders only with a channel_id, like the Archive button.
  const kind: ChannelKind =
    detail.channel_kind === 'announcement' ? 'announcement' : 'conversation';

  const save = async (
    body: GroupWrite,
    setInlineError: (message: string | null) => void
  ): Promise<boolean> => {
    setInlineError(null);
    try {
      await updateGroup.mutateAsync({ id: detail.id, body });
      return true;
    } catch (error) {
      if (conflictCode(error) === 'GROUP_ARCHIVED') {
        // Somebody archived the group while this drawer was open. The refetched
        // detail comes back not editable, so the page swaps in the read-only
        // drawer; the notice says why this save did nothing until it does.
        setArchived(true);
        void client.invalidateQueries({ queryKey: groupKeys.all });
        return false;
      }
      const message = getBusinessErrorMessage(error);
      if (message && INLINE_MESSAGES.has(message)) {
        setInlineError(message);
      } else {
        console.error('Failed to save group', error);
        showError('Could not save the group');
      }
      return false;
    }
  };

  // Only a switch sends kind. A PUT without it keeps the channel's kind, so
  // a member or name save cannot undo a switch made in another tab.
  const saveMembers = (members: EntryRef[]) =>
    save({ name: detail.name, members }, setMembersError);

  const saveName = async () => {
    const saved = await save(
      { name: draftName.trim(), members: entries },
      setNameError
    );
    if (saved) setRenaming(false);
  };

  const saveKind = (next: ChannelKind) =>
    save({ name: detail.name, members: entries, kind: next }, setMembersError);

  const confirmRemove = async () => {
    if (!pendingRemove) return;
    // Close either way, so an inline error below the roster is not hidden
    // behind the dialog.
    await saveMembers(removeEntry(entries, pendingRemove.entry));
    setPendingRemove(null);
  };

  const removeButton = (label: string, onClick: () => void) => (
    <Button variant='ghost' size='icon' aria-label={label} onClick={onClick}>
      <X />
    </Button>
  );

  return (
    <>
      <SheetHeader>
        {renaming ? (
          <div className='flex flex-col gap-2'>
            <SheetTitle className='sr-only'>{detail.name}</SheetTitle>
            <Input
              inputSize='sm'
              aria-label='Group name'
              value={draftName}
              maxLength={80}
              onChange={event => setDraftName(event.target.value)}
            />
            {nameError && (
              <p className='text-xs text-destructive'>{nameError}</p>
            )}
            <div className='flex gap-2'>
              <Button
                size='sm'
                disabled={draftName.trim() === '' || isGroupSaveBlocked}
                onClick={saveName}
              >
                Save
              </Button>
              <Button
                size='sm'
                variant='outline'
                onClick={() => {
                  setRenaming(false);
                  setDraftName(detail.name);
                  setNameError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className='flex min-w-0 items-center gap-2 pr-6'>
            <SheetTitle className='truncate'>{detail.name}</SheetTitle>
            <Button
              variant='ghost'
              size='icon'
              aria-label='Rename group'
              onClick={() => {
                setDraftName(detail.name);
                setRenaming(true);
              }}
            >
              <Pencil />
            </Button>
          </div>
        )}
      </SheetHeader>

      {archived && (
        <p className='text-xs text-muted-foreground'>{ARCHIVED_NOTICE}</p>
      )}

      {isDetailError && (
        <div className='flex items-center gap-2'>
          <p className='text-xs text-destructive'>
            Couldn&apos;t load the latest group.
          </p>
          <Button
            size='sm'
            variant='outline'
            disabled={isDetailFetching}
            onClick={onRetryDetail}
          >
            Retry
          </Button>
        </div>
      )}

      {detail.channel_id && (
        <GroupKindField
          value={kind}
          onChange={next => void saveKind(next)}
          disabled={isGroupSaveBlocked}
        />
      )}

      <div className='flex min-w-0 flex-1 flex-col gap-3'>
        <div className='flex items-baseline justify-between gap-2'>
          <h3 className='text-sm font-semibold'>Members</h3>
          <span className='text-xs text-muted-foreground'>
            {rosterHeadline(detail)}
          </span>
        </div>

        {buildRoster(detail).map(section => {
          const entry = section.entry;
          if (entry === null) {
            return (
              <div key='individual' className='flex flex-col'>
                <h4 className='text-sm font-medium'>Added individually</h4>
                {section.people.map(person => (
                  <MemberRow
                    key={person.user_id}
                    member={person}
                    action={removeButton(`Remove ${person.name}`, () =>
                      setPendingRemove({
                        entry: { kind: 'user', id: person.user_id },
                        title: `Remove ${person.name} from ${detail.name}?`,
                        message:
                          'They stop seeing new messages in this conversation. You can add them again later.',
                      })
                    )}
                  />
                ))}
              </div>
            );
          }
          return (
            <Collapsible key={entry.id} defaultOpen>
              <div className='flex min-w-0 items-center gap-2'>
                <EntryIcon entry={entry} />
                <span className='truncate text-sm font-medium'>
                  {entry.name}
                </span>
                <span className='whitespace-nowrap text-xs text-muted-foreground'>
                  {peopleCount(entry.holder_count ?? 0)}
                </span>
                <div className='ml-auto flex items-center'>
                  <CollapsibleTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      aria-label={`Show or hide ${entry.name}`}
                    >
                      <ChevronDown />
                    </Button>
                  </CollapsibleTrigger>
                  {removeButton(`Remove ${entry.name}`, () =>
                    setPendingRemove({
                      entry: toEntryRef(entry),
                      title: `Remove ${entry.name} from ${detail.name}?`,
                      message: removeEntryMessage(detail, entry),
                    })
                  )}
                </div>
              </div>
              <CollapsibleContent className='pl-6'>
                {section.people.map(person => (
                  <MemberRow
                    key={person.user_id}
                    member={person}
                    tag={
                      person.added_individually
                        ? 'also added individually'
                        : undefined
                    }
                  />
                ))}
              </CollapsibleContent>
            </Collapsible>
          );
        })}

        <MemberPicker
          selected={entries}
          pickDisabled={isGroupSaveBlocked}
          onPick={entry => saveMembers(addEntry(entries, entry))}
        />
        {membersError && (
          <p className='text-xs text-destructive'>{membersError}</p>
        )}
      </div>

      <SheetFooter className='flex-row justify-between gap-2'>
        <div className='flex flex-col items-start'>
          <Button
            variant='link'
            disabled={!channelId}
            onClick={() => {
              if (!channelId) return;
              // The chat sits under the drawer's scrim, so close the
              // drawer or the conversation opens where nobody can see it.
              onClose();
              openChatWindow(channelId);
            }}
          >
            Open conversation
          </Button>
          {detail.channel_id && !channelId && (
            <p className='text-xs text-muted-foreground'>
              Add yourself to open this conversation
            </p>
          )}
        </div>
        {detail.channel_id && (
          <Button variant='destructive' onClick={() => setArchiveOpen(true)}>
            Archive group
          </Button>
        )}
      </SheetFooter>

      <DestructiveConfirmDialog
        open={pendingRemove !== null}
        title={pendingRemove?.title ?? ''}
        message={pendingRemove?.message ?? ''}
        confirmLabel='Remove'
        pending={isGroupUpdatePending}
        confirmDisabled={isGroupSaveBlocked}
        onConfirm={confirmRemove}
        onCancel={() => setPendingRemove(null)}
      />
      {detail.channel_id && (
        <ArchiveGroupDialog
          groupName={detail.name}
          channelId={detail.channel_id}
          open={archiveOpen}
          onOpenChange={setArchiveOpen}
          onArchived={() => setArchiveOpen(false)}
        />
      )}
    </>
  );
}
